import { 
  Modal, 
  Thumbnail, 
  Text, 
  Box, 
  BlockStack,
  Filters,
  SkeletonBodyText,
  ResourceList,
  ResourceItem,
  EmptyState
} from "@medentic/polaris-react";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

interface CountryOption {
  label: string;
  value: string;
  flag: string;
  phoneCode: string;
}

interface CountrySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (value: string) => void;
  selectedValue: string | null;
  countryOptions: CountryOption[];
}

export function CountrySelectionModal({
  isOpen,
  onClose,
  onSelect,
  selectedValue: _selectedValue,
  countryOptions
}: CountrySelectionModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  void _selectedValue;

  // Filter options based on search query (search in both name and phone code)
  const filteredOptions = countryOptions.filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    option.phoneCode.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = useCallback((value: string) => {
    onSelect(value);
    setSearchQuery('');
  }, [onSelect]);

  const handleClose = useCallback(() => {
    onClose();
    setSearchQuery('');
  }, [onClose]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Convert options to ResourceList format
  const resourceListItems = filteredOptions.map(option => ({
    id: option.value,
    url: '',
    name: option.label,
    flag: option.flag,
    phoneCode: option.phoneCode,
  }));

  const renderItem = useCallback((item: typeof resourceListItems[0]) => {
    //const isSelected = _selectedValue === item.id;
    
    return (
      <ResourceItem
        id={item.id}
        url={item.url}
        onClick={() => handleSelect(item.id)}
        media={
          <Thumbnail 
            source={item.flag} 
            alt={item.name}
            size="small"
          />
        }
        accessibilityLabel={t("countrySelection.accessibility.select", { name: item.name })}
      >
        <BlockStack gap="100">
            <Text variant="bodyMd" as="p" fontWeight="medium">
                {item.name}
            </Text>
            <Text variant="bodySm" as="p" tone="subdued">
                {item.phoneCode}
            </Text>
        </BlockStack>
      </ResourceItem>
    );
  }, [handleSelect, t]);

  const renderModalContent = () => {
    if (countryOptions.length === 0) {
      return (
        <Box padding="100">
          <SkeletonBodyText lines={5} />
        </Box>
      );
    }
    
    if (filteredOptions.length === 0) {
      return (
        <EmptyState
          heading={t("countrySelection.emptyState.title")}
          image=""
        >
          <Text variant="bodyMd" as="p" tone="subdued">
            {t("countrySelection.emptyState.description")}
          </Text>
        </EmptyState>
      );
    }
    
    return (
      <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '0px' }}>
        <ResourceList
          resourceName={{
            singular: t("common.country"),
            plural: t("common.countries"),
          }}
          items={resourceListItems}
          renderItem={renderItem}
          showHeader={false}
        />
      </div>
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={t("common.pickACountry")}
      size="large"
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <Box padding="0">
            <BlockStack gap="0" >
            <Filters
                queryValue={searchQuery}
                queryPlaceholder={t("countrySelection.searchPlaceholder")}
                onQueryChange={setSearchQuery}
                onQueryClear={handleClearSearch}
                onClearAll={handleClearSearch}
                filters={[]}
                appliedFilters={[]}
            />
            
            {renderModalContent()}
            </BlockStack>
        </Box>
      </Modal.Section>
    </Modal>
  );
} 
